import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import PopupServiceImg from 'Assets/layout/dashboard/popup-service.png';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import Modal from 'Douleutaras/components/Modal/Modal';
import CustomDatepicker from 'Douleutaras/components/CustomDatepicker/CustomDatepicker';
import RegularButton from 'Douleutaras/components/RegularButton/RegularButton';
import i18n from 'Douleutaras/lib/i18n';
import { LANGUAGE_CODES } from 'Douleutaras/constants/system';
import { LANGUAGE_CONFIG } from 'Douleutaras/constants/localization';
import { bidBuyerStatusEndpoint, getBidBuyerStatus } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { jobBidsSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import moment from 'moment';

const FieldDatePicker = ({ value, onChange, error }: { value?: number; error?: string; onChange(val?: number): void; }) => {
  const { t } = useTranslation();
  const currentLang = i18n.language as LANGUAGE_CODES;
  const dateFormat = LANGUAGE_CONFIG.DATEPICKER.DISPLAY_FORMAT.DATE[currentLang];
  const onDateChange = (date: Date) => {
    onChange(date?.getTime());
  };

  return (
    <>
      <CustomDatepicker
        datepickerProps={{
          selected: value ? new Date(value) : undefined,
          onChange: onDateChange,
          placeholderText: t('DATEPICKER.SELECT-DATE'),
          dateFormat: dateFormat || 'dd/MM/yyyy'
        }}
      />
      { error && (
        <CustomError>
          {error}
        </CustomError>
      )}
    </>
  );
};

interface StartingDateModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  isInvalid: boolean;
  title: React.ReactElement;
  userMugshot: string;
  control: Control;
  errorMessage?: string;
  onSubmitClick(): void;
  onBack?(): void;
  setIsOpen(modalStatus): void;
}

export const StartingDateModal = ({
                               onSubmitClick,
                               isOpen,
                               setIsOpen,
                               title,
                               onBack,
                               isSubmitting,
                               isInvalid,
                               userMugshot,
                               control
                             }: StartingDateModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} dismissCallback={() => setIsOpen(false)}>
      <DefaultModal
        serviceIcon={PopupServiceImg}
        serviceIconAlt={<Trans>T.CCF.STARTING DATE.ICON-ALT</Trans>}
        title={title}
        backText={<Trans>T.CCF.STARTING DATE.BACK-TEXT</Trans>}
        backTextAriaLabel={<Trans>T.CCF.STARTING DATE.BACK-TEXT-ARIA</Trans>}
        closeAriaLabel={<Trans>T.CCF.STARTING DATE.CLOSE-ARIA</Trans>}
        closeButtonCallback={() => setIsOpen(false)}
        backButtonCallback={typeof onBack === 'function' ? onBack : () => false}
        footer={<RegularButton disabled={isSubmitting || isInvalid}
                               size="large"
                               title={<Trans>T.CCF.SAVE-BUTTON</Trans>}
                               onClick={onSubmitClick} />}
        userMugshot={userMugshot}
      >
        <div>
          <Controller
            name="jobStartDate"
            control={control}
            rules={{ required: t('T.CCF.STARTING DATE.VALIDATE REQUIRED DATE') as unknown as string }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FieldDatePicker value={value} onChange={onChange} error={error?.message as string} />
            )}
          />
        </div>
      </DefaultModal>
    </Modal>
  );
};

const StartingDateModalWrapper = props => {
  const { onSubmit, defaultStartDate } = props;
  const methods = useForm<{ jobStartDate?: number }>({
    mode: 'onChange',
    defaultValues: useMemo(() => ({ jobStartDate: defaultStartDate ?? undefined }), [defaultStartDate])
  });
  const { handleSubmit, control, formState: { isSubmitting, isValid } , reset } = methods;

  useEffect(() => {
    reset({ jobStartDate: defaultStartDate ?? undefined });
  }, [defaultStartDate, reset]);

  return (
    <StartingDateModal
      {...props}
      control={control}
      isSubmitting={isSubmitting}
      isInvalid={!isValid}
      onSubmitClick={handleSubmit(onSubmit)}
    />
  );
};

export default props => {
  const { bidId, jobId, isOpen } = props;
  if (!isOpen) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }
  const metaKey = bidBuyerStatusEndpoint(jobId, bidId);
  const dispatch = useAppDispatch();
  const bidBuyerStatus = useAppSelector(state => state.jobBidsStatusBuyer);
  const bid = useAppSelector(state => jobBidsSelectors.selectById(state, parseInt(bidId, 0)));
  const [defaultStartDate, setDefaultStartDate] = useState<number | undefined>(undefined);

  const initialize = async () => {
    // Check if the buyer status is fetched, if not, fetch it.
    if (typeof bidBuyerStatus?.meta?.[metaKey] === 'undefined' && isOpen) {
      await dispatch(getBidBuyerStatus({
          jobId,
          bidId
        })
      );
    }

    const currentBuyerSelectionsId = bidBuyerStatus?.meta?.[metaKey]?.data?.[0]?.id;
    const currentBuyerSelections = bidBuyerStatus?.entities?.[currentBuyerSelectionsId]?.attributes;

    const jobStartDate = currentBuyerSelections?.jobStartDate ?? bid?.attributes.start;
    if (jobStartDate) {
      setDefaultStartDate(moment(jobStartDate)
        .valueOf());
    } else {
      setDefaultStartDate(undefined);
    }
  };

  useEffect(() => {
    initialize();
  }, [ isOpen ]);

  return (
    <StartingDateModalWrapper { ...props } defaultStartDate={defaultStartDate} />
  );
};
